import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import { Breadcrumb } from '@/components/shared/Breadcrumb';
import { AccessRestricted } from '@/components/shared/AccessRestricted';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { SkeletonPage } from '@/components/skeletons';
import { ROUTES } from '@/routes/routes';
import { REQUEST_THREAD_KEY, requestThreadService } from '@/services/request-thread.service';
import { RequestThreadHeader } from './components/RequestThreadHeader';
import { RequestBlocks } from './components/RequestBlocks';
import { InspectionContextCard, QuotationContextCard } from './components/ThreadContextCards';
import { ThreadActionRegion, ThreadTimelineRegion } from './components/ThreadRegions';

/**
 * The staff Request Thread workspace — `/requests/:roundId`.
 *
 * ONE CUSTOMER REQUEST = ONE MODIFICATION ROUND = ONE REQUEST THREAD, and this
 * is where staff work it. Keyed by the round because the round IS the thread:
 * an inspection-keyed URL cannot address a closed request, and only one round
 * per job may be open at a time.
 *
 * ── ONE REQUEST, AND ONLY ONE ───────────────────────────────────────────────
 * Everything below comes from `GET /modification-rounds/:roundId/thread`. That
 * endpoint exists precisely so this page does not stitch the round, its history,
 * the job and the quotation together from four calls that can arrive out of step
 * — the failure mode the existing panels have today, and the seven-query
 * Authorization Case detail page has in a worse form. So: no job fetch, no
 * revision fetch, no entries fetch, no capability fetch. If a later sub-stage
 * needs more, it belongs in the composed read, not in a second request.
 *
 * ── THE SERVER DECIDES; THIS PAGE RENDERS ───────────────────────────────────
 * Ownership is `currentOwner` / `waitingFor`. Eligibility is `capabilities`,
 * computed server-side from state ∧ permission by probing the state machine
 * itself. Nothing here derives an action from a role name, a department name,
 * `assignedUserId`, or the workflow state alone. There is no client-side
 * authorization rule in this feature, by design — the route guard is a courtesy
 * and the backend answers 403 and 409 regardless of what renders.
 *
 * ── STRUCTURE, BUILT FOR WHAT COMES NEXT ────────────────────────────────────
 * The layout is final; two of its regions are not yet filled. `ThreadTimelineRegion`
 * is C-iv's, `ThreadActionRegion` is C-v's, and both already receive the whole
 * thread — so neither sub-stage needs to touch this file, its data flow or its
 * layout. C-vi migrates navigation INTO here; nothing points here yet.
 */

/** The HTTP status of a failure, or null when it was not an HTTP reply. */
function statusOf(error: unknown): number | null {
  return axios.isAxiosError(error) ? (error.response?.status ?? null) : null;
}

function ThreadShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-5 pb-4">
      <Breadcrumb
        segments={[{ label: 'Inspections', to: ROUTES.INSPECTIONS }, { label: 'Request Thread' }]}
      />
      {children}
    </div>
  );
}

/**
 * Not found.
 *
 * The server answers 404 for a round that does not exist AND for one belonging
 * to another tenant — it folds both predicates into a single lookup and cannot
 * tell them apart. This screen must not try to either: one message, no hint that
 * the id might be real somewhere else, no retry that would let someone probe.
 */
function ThreadNotFound() {
  return (
    <div
      role="status"
      data-testid="thread-not-found"
      className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border px-6 py-16 text-center"
    >
      <FontAwesomeIcon
        icon={faFileCircleQuestion}
        className="mb-4 h-10 w-10 text-muted-foreground/60"
      />
      <h2 className="text-base font-medium text-foreground">This request could not be found.</h2>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">
        The link may be out of date, or the request may have been removed. Open the job to see its
        current requests.
      </p>
    </div>
  );
}

export function RequestThreadPage() {
  const { roundId } = useParams<{ roundId: string }>();

  const query = useQuery({
    queryKey: [REQUEST_THREAD_KEY, roundId],
    queryFn: () => requestThreadService.getThread(roundId as string),
    enabled: !!roundId,
    // A refusal and a missing record are both final answers. Retrying either
    // wastes a round trip and, on 404, repeats a probe.
    retry: (failureCount, error) => {
      const status = statusOf(error);
      if (status === 403 || status === 404) return false;
      return failureCount < 1;
    },
  });

  if (query.isLoading) {
    return (
      <div data-testid="thread-loading">
        <SkeletonPage />
      </div>
    );
  }

  if (query.isError) {
    const status = statusOf(query.error);
    // 403 means the route guard was bypassed or the permission changed under the
    // user. The backend is the boundary and it has refused; say so plainly and
    // name nothing — not the permission, not the endpoint.
    if (status === 403) {
      return (
        <ThreadShell>
          <AccessRestricted description="Your role does not include access to this request." />
        </ThreadShell>
      );
    }
    if (status === 404) {
      return (
        <ThreadShell>
          <ThreadNotFound />
        </ThreadShell>
      );
    }
    return (
      <ThreadShell>
        <QueryErrorState subject="this request" onRetry={() => void query.refetch()} />
      </ThreadShell>
    );
  }

  const thread = query.data;
  if (!thread) {
    // Defensive only: `isLoading` and `isError` are exhaustive in practice.
    return (
      <ThreadShell>
        <QueryErrorState subject="this request" onRetry={() => void query.refetch()} />
      </ThreadShell>
    );
  }

  return (
    <div className="space-y-5 pb-4" data-testid="request-thread">
      <Breadcrumb
        segments={[
          { label: 'Inspections', to: ROUTES.INSPECTIONS },
          {
            label: thread.inspection.inspectionNumber,
            to: ROUTES.INSPECTION_DETAIL.replace(':id', thread.inspection.id),
          },
          { label: `Request #${thread.roundNumber}` },
        ]}
      />

      <RequestThreadHeader thread={thread} />

      {/* The request itself comes first and full width: it is the thing the
          reader came for, and on a narrow screen it must not sit below a stack
          of context cards. */}
      <RequestBlocks thread={thread} />

      {/* Working column beside context. Stacks to one column under `lg`, with the
          work above the reference material rather than below it. */}
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <ThreadActionRegion thread={thread} />
          <ThreadTimelineRegion thread={thread} />
        </div>
        <aside className="space-y-5">
          <InspectionContextCard thread={thread} />
          <QuotationContextCard thread={thread} />
        </aside>
      </div>
    </div>
  );
}
